'use client';

// cSpell:ignore subtitl

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type {
  OneLineTwoIndexData,
  OneLineTwoItem,
  OneLineTwoProps,
  OneLineTwoTrackingParams,
} from './types';

const COMPONENT_ID = 'oneLineTwo';
const DEFAULT_TITLE_COLOR = 'var(--color-primary-black)';
const DEFAULT_CARD_TEXT_COLOR = 'var(--color-primary-white)';

const formatText = (text?: string) => text?.replace(/<br>/g, '\n') || '';

const getSubtitleColor = (indexData: OneLineTwoIndexData) =>
  indexData.layout_subtitle_color || indexData.layout_subtitl_color || DEFAULT_TITLE_COLOR;

const getImageRatio = (item: OneLineTwoItem) => {
  if (item.jump_img_ratio) return String(item.jump_img_ratio);
  if (item.jump_img_width && item.jump_img_height) {
    return `${item.jump_img_width} / ${item.jump_img_height}`;
  }
  return '370 / 500';
};

const getImageSize = (value: string | number | undefined, fallback: number) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : fallback;
};

const getAlt = (index: number, item: OneLineTwoItem, moduleTitle: string) =>
  item.title ||
  item.sku?.short_name ||
  item.product_group?.name ||
  `${moduleTitle || COMPONENT_ID} ${index + 1}`;

const buildTrackingParams = (
  item: OneLineTwoItem,
  originalIndex: number,
  componentId: string,
  moduleId: string,
  moduleTitle: string,
  moduleIndex: number,
): OneLineTwoTrackingParams => ({
  module_index: moduleIndex,
  module_name: componentId,
  module_id: moduleId,
  mkt_index: originalIndex,
  mkt_index_sub: '',
  mkt_jump_type: String(item.jump_type || ''),
  mkt_jump_content: String(item.Jump_target || ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || item.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
  module_title: moduleTitle,
});

const buildJumpProps = (item: OneLineTwoItem) => ({
  jump_type: item.jump_type,
  Jump_target: item.Jump_target,
  ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
  ...(item.product_group?.id && {
    product_group: { id: String(item.product_group.id), path: item.product_group.path },
  }),
});

const useTrackingHandlers = (
  trackingParams: OneLineTwoTrackingParams,
  onTrack?: (params: OneLineTwoTrackingParams) => void,
  onExpTrack?: (params: OneLineTwoTrackingParams) => void,
  onValidExpTrack?: (params: OneLineTwoTrackingParams) => void,
) => {
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onExpTrack?.(trackingParams);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    onTrack?.(trackingParams);
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

function OneLineTwoCard({
  item,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: OneLineTwoItem;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  onTrack?: (params: OneLineTwoTrackingParams) => void;
  onExpTrack?: (params: OneLineTwoTrackingParams) => void;
  onValidExpTrack?: (params: OneLineTwoTrackingParams) => void;
}) {
  const trackingParams = useMemo(
    () => buildTrackingParams(item, originalIndex, componentId, moduleId, moduleTitle, moduleIndex),
    [componentId, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );
  const cardRef = useMergedRefs(expRef, validExposureRef);

  const jumpProps = useMemo(() => buildJumpProps(item), [item]);
  const titleColor = item.title_color || DEFAULT_CARD_TEXT_COLOR;
  const descColor = item.des_color || titleColor;
  const titleColorStyle = useMemo(() => ({ color: titleColor }), [titleColor]);
  const descColorStyle = useMemo(() => ({ color: descColor }), [descColor]);
  const alt = getAlt(originalIndex, item, moduleTitle);
  const imageWidth = getImageSize(item.jump_img_width, 370);
  const imageHeight = getImageSize(item.jump_img_height, 500);

  return (
    <div ref={cardRef} className="min-w-0">
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={alt}
        className="block"
      >
        <div
          className="relative w-full overflow-hidden bg-auxiliary-light-gray"
          style={{ aspectRatio: getImageRatio(item) }}
        >
          <Image
            src={item.jump_image || ''}
            alt={alt}
            width={imageWidth}
            height={imageHeight}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 text-primary-white">
            <div className="absolute inset-x-10 bottom-[33px]">
              {item.title && (
                <h3
                  className="font-be text-[18px] leading-22 line-clamp-1 whitespace-pre-line"
                  style={titleColorStyle}
                >
                  {formatText(item.title)}
                </h3>
              )}
              {item.des && (
                <p
                  className="font-ir text-[12px] leading-16 line-clamp-2 whitespace-pre-line"
                  style={descColorStyle}
                >
                  {formatText(item.des)}
                </p>
              )}
            </div>
            <span
              aria-hidden
              className="absolute bottom-8 left-10 block h-[18px] w-[18px] bg-[url('/zeelool-next/one-line-two-arrow-right.png')] bg-left bg-contain bg-no-repeat"
            />
          </div>
        </div>
      </MagicJump>
    </div>
  );
}

export default function OneLineTwo({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: OneLineTwoProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const validItems = useMemo(() => {
    const rawItems = indexData.jump_target || [];
    return rawItems
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => Boolean(item.jump_image));
  }, [indexData.jump_target]);

  if (deviceType === EPlatform.WEB || validItems.length === 0) {
    return null;
  }

  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const moduleTitle = indexData.layout_title || '';
  const shouldShowHeader = Number(indexData.layout_title_status) === 1;
  const subtitleColor = getSubtitleColor(indexData);
  const titleStyle = { color: indexData.layout_title_color || DEFAULT_TITLE_COLOR };
  const subtitleStyle = { color: subtitleColor };

  return (
    <section data-component-id={componentId} className="mt-[40px] mb-[20px]">
      {shouldShowHeader && (indexData.layout_title || indexData.layout_subtitle) && (
        <header className="mb-20 text-center">
          {indexData.layout_title && (
            <h2
              className="mx-auto px-[24px] font-be text-[24px] leading-29 line-clamp-3 whitespace-pre-line text-primary-black"
              style={titleStyle}
            >
              {formatText(indexData.layout_title)}
            </h2>
          )}
          {indexData.layout_subtitle && (
            <p
              className="mx-[19px] mt-8 font-ir text-[12px] leading-18 line-clamp-3 whitespace-pre-line text-primary-black"
              style={subtitleStyle}
            >
              {formatText(indexData.layout_subtitle)}
            </p>
          )}
        </header>
      )}
      <div className="grid grid-cols-2 gap-5">
        {validItems.map(({ item, originalIndex }) => (
          <OneLineTwoCard
            key={item.id || `one-line-two-${originalIndex}`}
            item={item}
            originalIndex={originalIndex}
            componentId={componentId}
            moduleId={moduleId}
            moduleTitle={moduleTitle}
            moduleIndex={moduleIndex}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </div>
    </section>
  );
}
